// ServiceContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import AccountCodeContainer from 'src/models/AP/AccountCodeContainer';
import { VSResquestType } from 'src/components/project-billing/models/CreateInvoiceValidation';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import ExpenseCodeContainer from '../models/ExpenseCodeContainer';
import UserExpense from 'src/models/AP/EmployeeExpense';
import ErrorResponse from 'src/models/ErrorResponse';
import { AnyObject } from 'final-form';
import useFetch from 'use-http';

export const TE_URL = process.env.REACT_APP_BASE_TE_API_URL;
const CREATE_DAY_ENTRY = 'api/v2/entry/create';
const EXPENSE_CODES = 'api/v2/expense/codes/';
const EXPENSE_RECEIPT = 'api/v2/expense/receipt/';
const CURRENT_PAY_PERIOD = 'api/v2/PayPeriod/currentpayperiod/';
const ADMIN_NAV_URL = '/api/v2/payPeriodForAdmin/';
const VALIDATE_PROSPECT = 'api/v2/entry/validateProspect';
const VALIDATE_SECTION = 'api/v2/entry/validateSectionUnit/isTurnkeyBilling/';
const DELETE_DAY_ENTRY = 'api/v2/entry/delete/';
const CUTOFF_DATE = 'api/v2/entry/cutoffDate/tsdinumber/';

export type MileageRequest = {
  clientNumber: number;
  miles: string;
  payPeriodNumber: string;
  prospectNumber: number;
};

export type MileageRequestContainer = {
  miles: string;
};

export type VDEResquestType = {
  clientNumber: number;
  prospectNumber: number;
  accountNumber: number;
  sectionNumber?: number;
  unitNumber?: number;
  hours: number;
  tmId: number;
  compensationType: string;
  periodNo: string;
  tmDate: string;
  tsdNo: string;
  workDescription: string;
  selectedAccountCodeContainer: AnyObject;
  userExpenses: Array<AnyObject>;
};

export type CERequestType = {
  mileageRequestContainer: MileageRequestContainer;
  userExpense: UserExpense;
};

export type VPTRequestType = {
  tsdiNumber: string;
  payPeriodNumber: string;
};

export type PDEResquestType = {
  sectionNumber?: number;
  unitNumber?: number;
} & VDEResquestType;

type ServiceState = {
  expenseCodes: Array<ExpenseCodeContainer> | undefined;
  teUserData: AnyObject | undefined;
  accountCodes: Array<AccountCodeContainer> | undefined;
  teUserVacTime: AnyObject | undefined;
  refresh: boolean | undefined;
  updateReceipts?: AnyObject;
  timeAndExpenseToken: string | undefined;
};

// --- Define the interface for the service functions ---
export interface IServiceContextValue {
  serviceState: ServiceState;
  getTEData: (updateState: boolean) => Promise<void | object | AnyObject>;
  createDayEntry: (body: PDEResquestType) => Promise<void | object | undefined>;
  deleteReceipt: (id: number) => Promise<number>;
  emailReceipt: (id: number) => Promise<number>;
  deleteDayEntry: (id: number) => Promise<void | AnyObject | undefined>;
  newReceiptInfo: (info: AnyObject) => Promise<AnyObject>;
  getTimePeriodCutoffDate: (period: string, tsdiNumber: number) => Promise<AnyObject>;
  validateBillingCode: (clientNumber: number, projectNumber: number, subcode: string, altSubcode: string) => Promise<void | object>;
  adminGetUserInfo: (payPeriod: string, userId: string) => Promise<void | object | AnyObject>;
  getReceiptForExpense: (expenseRecordId: number) => Promise<void | object | AnyObject>;
  deleteExpense?: (esId: number) => Promise<number | undefined>;
  getMileage?: (body: MileageRequest) => Promise<number | undefined>;
  uploadReceipt?: (body: AnyObject, esId: number) => Promise<number | undefined>;
  editExpense?: (body: UserExpense) => Promise<number | undefined>;
  createExpense?: (body: CERequestType) => Promise<AnyObject | undefined>;
  getExpenseCodes?: (token: string) => Promise<AnyObject | undefined>;
  validateSection?: (body: VSResquestType, payPeriod: string, isTurnKey?: boolean) => Promise<void | object>;
}

const handleApiError = (value: Response) => {
    console.log('api call failed: ' + value);
};

const handleApiCall = (value: Response) => {
    return new Promise<object>((resolve) => {
        if (value.ok) {
            value
                .json()
                .then((body) => resolve(body))
                .catch(() => resolve(null));
        } else {
            resolve(value.json().then((errorResponse: ErrorResponse) => {
              if (errorResponse?.data && !errorResponse?.message && !errorResponse?.messageCode) {
                return new ErrorResponse('500', errorResponse.data );
              } else {
                return new ErrorResponse(errorResponse.messageCode, errorResponse.message );
              }
            }));
        }
    });
};

const createHeader = (type: string, teToken: string) => {
    let headers = {};
    if (type === 'getTeData') {
      headers = { 'Tsjwt': teToken, 'content-type': 'application/json' };
    }

    return headers;
};

const getRequest = async (url: string, type: string | undefined, jwt: string | undefined = undefined) => {
    return fetch(url, getHeader(type, jwt)).then(handleApiCall).catch(handleApiError);
};

const postHeader = (body: object, jwt: string | undefined = undefined) => {
  const header: RequestInit = {
      body: JSON.stringify(body),
      headers: createHeader('getTeData', jwt),
      method: 'POST',
      mode: 'cors',
  };
  return header;
};

const deleteHeader = (jwt: string | undefined = undefined) => {
  const header: RequestInit = {
      headers: createHeader('getTeData', jwt),
      method: 'DELETE',
      mode: 'cors',
  };
  return header;
};

const deleteRequest = async (url: string, jwt: string | undefined = undefined) => {
  return fetch(url, deleteHeader(jwt)).then(handleApiCall).catch(handleApiError);
};

const postRequest = async (url: string, body: object, jwt: string | undefined = undefined) => {
    return fetch(url, postHeader(body, jwt)).then(handleApiCall).catch(handleApiError);
};

const getHeader = (type: string | undefined, jwt: string | undefined) => {
    const header: RequestInit = {
        headers: createHeader(type, jwt),
        method: 'GET',
        mode: 'cors',
    };
    return header;
};

// --- Create the context ---
const ServiceContext = createContext<IServiceContextValue | undefined>(undefined);

// --- Provider component ---
export const ServiceContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { get, /*post,*/ response } = useFetch();
  const appContext = React.useContext<IAppContextState>(AppContext);
  const [serviceState, setServiceState] = useState<ServiceState>({
    expenseCodes: undefined,
    timeAndExpenseToken: undefined,
    teUserData: undefined,
    accountCodes: undefined,
    teUserVacTime: undefined,
    refresh: undefined,
    updateReceipts: undefined
  });

  useEffect(() => {
    if (!serviceState.timeAndExpenseToken) {
      getTimeAndExpenseToken();
    }
  }, []);

  useEffect(() => {
    if (!!serviceState.timeAndExpenseToken) {
      getExpenseCodes(serviceState.timeAndExpenseToken);
    }
  }, [serviceState.timeAndExpenseToken]);

  useEffect(() => {
    if (!!serviceState.expenseCodes) {
      console.log(serviceState.expenseCodes);
    }
  }, [serviceState.expenseCodes]);

  const decodeJwtPayload = (token: string) => {
    try {
        const payload = token.split('.')[1];
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
        atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.log('Invalid JWT token');
        return null;
    }
};

  const getTimeAndExpenseToken = async () => {
    let userName = appContext.currentUser.username;

    const result = await get(`v1.0/reports/time-expense-token/user/${userName}`);
    if (response.ok) {
        try {
                const jwtObject = JSON.parse(result);
                setServiceState((prev) => ({
                  ...prev,
                  timeAndExpenseToken: jwtObject.timeAndExpenseJwt
                }));
            } catch (error) {
                console.log('Invalid JSON string:', error);
            }
    } else {
        console.log('Unable to get T&E token!');
    }
  };

  const getTimePeriodCutoffDate = async (period: string, tsdiNumber: number) => {
    try {
      const token = serviceState.timeAndExpenseToken;
      let result = await getRequest(`${TE_URL}${CUTOFF_DATE}${tsdiNumber}/payPeriod/${period}`, 'getTeData', token);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
          return Promise.resolve(result as Date);
      } else {
          const message = 'Unable to get T&E cutuff date!';
          return Promise.reject(message);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  };

  // All functions can be implemented to call your backend as needed
  const validateBillingCode = async (clientNumber: number, projectNumber: number, subcode: string, altSubcode: string) => {
    try {
      const token = serviceState.timeAndExpenseToken;
      const payload = {
          'clientNumber': clientNumber,
          'prospectNumber': projectNumber,
          'sectionNumber': subcode,
          'unitNumber': altSubcode };
      let vbResponseData = await postRequest(`${TE_URL}${VALIDATE_PROSPECT}`, payload as object, token);
      if (response.ok && (!(vbResponseData as {error: string})?.error || (vbResponseData as {error: string})?.error?.length === 0)) {
          return Promise.resolve(vbResponseData);
      } else {
        const message = 'Unable to validate Billing code.';
        return Promise.reject(message);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getTEData = async (updateState: boolean) => {
    try {
      let token = serviceState.timeAndExpenseToken;
      const decodedJwt =  decodeJwtPayload(token);
      let tsdiNumber = decodedJwt.tn;
      let userRole = decodedJwt.ur;
      const result = await getRequest(`${TE_URL}${CURRENT_PAY_PERIOD}${tsdiNumber}`, 'getTeData', token);

      if (!!updateState) {
        setServiceState((prev) => ({
          ...prev,
          teUserData: { ...result, userRole: userRole },
          refresh: undefined
        }));

        return Promise.resolve(result);
      } else {
        return Promise.resolve(result);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const deleteReceipt = async (id: number): Promise<number> => {
    // Example placeholder
    return 200;
  };

  const emailReceipt = async (receiptId: number): Promise<number> => {
    // Example placeholder returning HTTP status
    return 200;
  };

  const newReceiptInfo = async (info: AnyObject): Promise<AnyObject> => {
    // Example placeholder
    return {};
  };

  const adminGetUserInfo = async (period: string, userId: string) => {
    try {
      const result = await getRequest(`${TE_URL}${ADMIN_NAV_URL}${period}/otheremployee/${userId}`, 'getTeData', serviceState.timeAndExpenseToken);
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getReceiptForExpense = async (expenseRecordId: number) => {
    try {
      const result = await getRequest(`${TE_URL}${EXPENSE_RECEIPT}${expenseRecordId}`, 'getTeData', serviceState.timeAndExpenseToken);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service');
    } else {
      console.log('Unable to get receipt.');
    }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const deleteDayEntry = async (tmId: number) => {
    try {
      const result = await deleteRequest(`${TE_URL}${DELETE_DAY_ENTRY}${tmId}`, serviceState.timeAndExpenseToken);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to delete day entry.');
    }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const createDayEntry = async (body: PDEResquestType) => {
    try {
      const result = await postRequest(`${TE_URL}${CREATE_DAY_ENTRY}`, body, serviceState.timeAndExpenseToken);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to create day entry.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getExpenseCodes = async (token: string): Promise<ExpenseCodeContainer[]> => {
    try {
      const result = await getRequest(`${TE_URL}${EXPENSE_CODES}`, 'getTeData', token);
  
      // Filtering unwanted fields and adding new expenses
      const expenses = (result as ExpenseCodeContainer[]) ?? [];
      let otherExpense: ExpenseCodeContainer | undefined;
  
      const filteredExpenses = expenses
        .map((e) => {
          switch (e?.code) {
            case 'P':
            case 'W':
            case 'X':
            case 'T':
              return undefined; // skip
            case 'O':
              otherExpense = e; // save for later
              return undefined;
            default:
              return e;
          }
        })
        .filter((e): e is ExpenseCodeContainer => !!e);
  
      if (otherExpense) {
        filteredExpenses.push(otherExpense);
      }
  
      setServiceState((prev) => ({
        ...prev,
        expenseCodes: filteredExpenses,
      }));
  
      // ✅ Always return a value here
      return filteredExpenses;
    } catch (e) {
      // ✅ Explicitly reject so callers can handle errors
      return Promise.reject(e);
    }
  };

  const validateSection = async (body: VSResquestType, payPeriod: string, isTurnKey: boolean = false) => {
    try {
      if (isTurnKey !== undefined) {
        let validateSectionUrl = `${VALIDATE_SECTION}${isTurnKey}/payPeriod/${payPeriod}`;

        const result = await postRequest(`${TE_URL}${validateSectionUrl}`, body, serviceState.timeAndExpenseToken);

        return (result);
      } else {
        return undefined;
      }
    } catch (e) {
      return Promise.reject(e);
    }
  };

  // --- Provide all functions in context value ---
  const value: IServiceContextValue = {
    serviceState,
    getTEData,
    deleteReceipt,
    createDayEntry,
    emailReceipt,
    deleteDayEntry,
    newReceiptInfo,
    adminGetUserInfo,
    getReceiptForExpense,
    validateBillingCode,
    getTimePeriodCutoffDate,
    getExpenseCodes,
    validateSection
  };

  return serviceState.timeAndExpenseToken === null ? <div>Loading...</div> : (
    <ServiceContext.Provider value={value}>{children}</ServiceContext.Provider>
  );
};

// --- Hook for components to consume the service context ---
export const useServiceCtx = (): IServiceContextValue => {
  const ctx = useContext(ServiceContext);
  if (!ctx) {
    throw new Error('useServiceCtx must be used within ServiceContextProvider');
  }
  return ctx;
};


// WEBPACK FOOTER //
// ./src/components/project-billing/components/TimeAndExpenseServiceContext.tsx